import {
  type Position, type TurnResult, type EndGameScore, type LevelConfig,
  Animal, Terrain,
} from "./types"
import { Tile } from "./tile"
import { Grid } from "./grid"
import { findIslands } from "./island"
import { resolvePredation } from "./predation"
import { Market } from "./market"
import { buildDeck, shuffle } from "./deck"
import { placementScore, updateCombo, deathPenalty, endGameScore } from "./scoring"
import { TutorialManager, type TutorialTrigger } from "./tutorial"
import { getLevelConfig, COMBO_MAX } from "./constants"

export interface GameCallbacks {
  onTurnStart?: (state: GameState) => void
  onTilePlaced?: (state: GameState, pos: Position) => void
  onPredation?: (state: GameState, kills: { pos: Position; animal: Animal }[]) => void
  onTurnEnd?: (state: GameState, result: TurnResult) => void
  onGameEnd?: (state: GameState, score: EndGameScore) => void
  onTutorialMessage?: (trigger: TutorialTrigger) => void
}

export class GameState {
  grid: Grid
  deck: Tile[]
  market: Market
  score: number
  comboCurrent: number
  comboBest: number
  turn: number
  initialSheepCount: number
  deadHerbivores: number
  placementTotal: number
  penaltyTotal: number
  level: number
  config: LevelConfig
  tutorial: TutorialManager
  callbacks: GameCallbacks
  gameOver: boolean
  selectedTile: Tile | null
  lastTurnResult: TurnResult | null

  constructor(level: number = 4, seed?: number, callbacks: GameCallbacks = {}, tutorialMessages?: Record<string, string>) {
    this.level = level
    this.config = getLevelConfig(level)
    this.grid = new Grid(this.config.gridSize)
    this.market = new Market(this.config.marketSize)
    this.score = 0
    this.comboCurrent = 1
    this.comboBest = 1
    this.turn = 0
    this.initialSheepCount = 0
    this.deadHerbivores = 0
    this.placementTotal = 0
    this.penaltyTotal = 0
    this.tutorial = new TutorialManager(level, tutorialMessages)
    this.callbacks = callbacks
    this.gameOver = false
    this.selectedTile = null
    this.lastTurnResult = null

    // Build deck based on level
    if (level >= 4) {
      this.deck = buildDeck(seed)
    } else {
      this.deck = this.buildTutorialDeck(level, seed)
    }

    // Count initial sheep in deck
    this.initialSheepCount = this.deck.filter(t => t.animal === Animal.Sheep).length

    // Place starting tile at center
    const center = Math.floor(this.config.gridSize / 2)
    const startTile = Tile.create(Terrain.Prairie, null, "all_land")
    this.grid.set({ row: center, col: center }, startTile)

    // Initial market fill
    this.market.refill(this.deck)
  }

  private buildTutorialDeck(level: number, seed?: number): Tile[] {
    const tiles: Tile[] = []

    switch (level) {
      case 0:
        // 3 tiles: prairies + 1 sheep
        tiles.push(Tile.create(Terrain.Prairie, null, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, null, "all_land"))
        break
      case 1:
        // 8 tiles: 4 sheep, 1 wolf, 3 empty
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, null, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, null, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Wolf, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, null, "all_land"))
        break
      case 2:
        // 12 tiles: 5 sheep, 1 wolf, 2 rivers, 1 cliff, 3 empty
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, null, "river_vertical"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, null, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Wolf, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, null, "cliff_north"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, null, "river_vertical"))
        tiles.push(Tile.create(Terrain.Prairie, null, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, null, "all_land"))
        break
      case 3:
        // 15 tiles: forests, 1 wolf, sheep, deer, mountains
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Forest, null, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Forest, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, null, "all_land"))
        tiles.push(Tile.create(Terrain.Mountain, null, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Wolf, "all_land"))
        tiles.push(Tile.create(Terrain.Forest, Animal.Deer, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, null, "all_land"))
        tiles.push(Tile.create(Terrain.Forest, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        tiles.push(Tile.create(Terrain.Mountain, null, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, null, "all_land"))
        tiles.push(Tile.create(Terrain.Forest, null, "all_land"))
        tiles.push(Tile.create(Terrain.Prairie, Animal.Sheep, "all_land"))
        break
    }

    if (seed !== undefined) shuffle(tiles, seed)
    return tiles
  }

  /** Pick a tile from the market */
  pickTile(marketIndex: number): Tile | null {
    if (this.gameOver) return null
    const tile = this.market.pick(marketIndex)
    if (tile) this.selectedTile = tile
    return tile
  }

  /** Rotate the currently selected tile */
  rotateSelectedTile(): void {
    if (this.selectedTile) {
      this.selectedTile.rotate(1)
    }
  }

  /** Get valid placements for the current state */
  getValidPlacements(): Position[] {
    return this.grid.getValidPlacements()
  }

  /** Place the selected tile and execute the full turn loop */
  placeTile(pos: Position): TurnResult | null {
    if (this.gameOver || !this.selectedTile) return null
    if (!this.grid.canPlace(pos)) return null

    this.turn++

    // 1. Place tile
    this.grid.set(pos, this.selectedTile)
    this.callbacks.onTilePlaced?.(this, pos)

    // 2. Tutorial: tile_placed
    const placedTrigger = this.tutorial.check("tile_placed", { tile: this.selectedTile })
    if (placedTrigger) this.callbacks.onTutorialMessage?.(placedTrigger)

    // 3. Score placement
    const pScore = placementScore(this.comboCurrent)
    this.placementTotal += pScore

    // 4. Find islands and resolve predation on all of them
    const islands = findIslands(this.grid)
    let totalHerbivoreDeaths = 0
    const allKills: { pos: Position; animal: Animal }[] = []

    for (const island of islands) {
      const predResult = resolvePredation(this.grid, island)
      totalHerbivoreDeaths += predResult.herbivoreDeaths
      allKills.push(...predResult.kills)
    }

    if (allKills.length > 0) {
      this.callbacks.onPredation?.(this, allKills)
      // Tutorial: predation_occurred
      const predTrigger = this.tutorial.check("predation_occurred", { predationOccurred: true })
      if (predTrigger) this.callbacks.onTutorialMessage?.(predTrigger)
    }

    // 5. Apply penalties
    const penalty = deathPenalty(totalHerbivoreDeaths)
    this.penaltyTotal += penalty
    this.deadHerbivores += totalHerbivoreDeaths

    // 6. Update combo
    const comboReset = totalHerbivoreDeaths > 0
    this.comboCurrent = updateCombo(this.comboCurrent, totalHerbivoreDeaths, COMBO_MAX)
    this.comboBest = Math.max(this.comboBest, this.comboCurrent)

    // 7. Calculate turn score
    const turnScore = pScore + penalty
    this.score += turnScore

    // 8. Refill market
    this.market.refill(this.deck)
    this.selectedTile = null

    // 9. Tutorial: market check
    const marketTrigger = this.tutorial.checkMarket(this.market.getTiles())
    if (marketTrigger) this.callbacks.onTutorialMessage?.(marketTrigger)

    const result: TurnResult = {
      placementScore: pScore,
      predationResult: { kills: allKills, herbivoreDeaths: totalHerbivoreDeaths },
      penaltyScore: penalty,
      comboReset,
      turnScore,
    }
    this.lastTurnResult = result

    // 10. Check end of game
    if (this.isGameOver()) {
      this.gameOver = true
      const finalScore = this.calculateEndGameScore()
      this.callbacks.onGameEnd?.(this, finalScore)
    } else {
      // Tutorial: turn_start for next turn
      this.callbacks.onTurnStart?.(this)
      const turnTrigger = this.tutorial.check("turn_start")
      if (turnTrigger) this.callbacks.onTutorialMessage?.(turnTrigger)
    }

    this.callbacks.onTurnEnd?.(this, result)
    return result
  }

  /** Check if game is over */
  isGameOver(): boolean {
    if (this.deck.length === 0 && this.market.isEmpty) return true
    // No valid placements
    if (this.market.size > 0 && this.grid.getValidPlacements().length === 0) return true
    return false
  }

  /** Calculate final score */
  calculateEndGameScore(): EndGameScore {
    return endGameScore(
      this.grid,
      this.placementTotal,
      this.penaltyTotal,
      this.comboBest,
      this.initialSheepCount,
    )
  }
}
